/**
 * The employee file (piece 34, *The employee file*; decision 3180). 🔒 One HR
 * page per job at HR › Employees › the person, read by holders of
 * `hr.employees.view` and by a manager for each person in their own management
 * chain, read-only; and My Info, the signed-in person's own copy, opened from
 * the user menu. 🔨 The Person page carries none of this any more.
 *
 * 🔒 Each section keeps the audience its record already had: the reader is
 * resolved once a page, and the sections are read off that one answer, so no
 * table states an audience of its own.
 */

import type { ActiveCredentialRow } from './clockCredentials';
import type { EmergencySection } from './emergencyContacts';
import type { ReviewsSection } from './reviews';
import type { EmployeeEditStart } from './hire';
import type { PaySection } from './pay';
import type { WorkingPatternSection } from './workingPattern';
import type { TimeCorrectionsSection } from './timeCorrections';
import type { TimeOffBegunSection } from './timeOff';
import type { TimeOffEnrollmentSection } from './timeOffPlans';

/** The Employee page's own address, one per job. */
export const HR_EMPLOYEE_ROUTE = '/hr/employees/:employmentId';
/** My Info, the signed-in person's own file, opened from the user menu beside Account Settings. */
export const MY_INFO_ROUTE = '/my-info';
/** The route the browser asks for My Info, relative to the API root. */
export const MY_INFO_PATH = '/hr/my-info';

/** The page a person opens for one job's file. */
export function employeeRoute(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}`;
}

/** The route the browser asks for one job's file, relative to the API root. */
export function employeeFilePath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}`;
}

/** The refusal the Employee page's route answers anybody outside its audience, in a sentence. */
export const EMPLOYEE_FILE_REFUSAL = 'The Employee page is open to HR and to a manager above this person, and this identity is neither.';

/**
 * Who is reading. 🔒 `hr` holds the HR pages permission; `chain` is a manager
 * above this job on `bossEmploymentId` as at today; `self` is the employee on
 * My Info. A reader is resolved in that order, so an HR holder reading their
 * own job on the HR page reads it as HR.
 */
export const EMPLOYEE_FILE_READERS = ['hr', 'chain', 'self'] as const;
export type EmployeeFileReader = (typeof EMPLOYEE_FILE_READERS)[number];

/** The page's sections, in the order they draw. */
export const EMPLOYEE_FILE_SECTIONS = ['job', 'pay', 'timeOff', 'place', 'access', 'emergency', 'time', 'attendance', 'trainings', 'reviews', 'custody'] as const;
export type EmployeeFileSectionKey = (typeof EMPLOYEE_FILE_SECTIONS)[number];

/**
 * 🔒 Who reads each section (decision 3180): each keeps the audience its
 * record had on the Person page. Pay, Access and Custody are HR, Admin and the
 * employee; Job and Trainings reach the management chain too; Time reaches the
 * employee and the chain, because HR reads the pay and not the hours, and its
 * Clock Credential History rows are HR's and the employee's alone.
 */
export const SECTION_READERS: Readonly<Record<EmployeeFileSectionKey, readonly EmployeeFileReader[]>> = {
  job: ['hr', 'chain', 'self'],
  pay: ['hr', 'self'],
  // 🔒 Decision 13030 (piece 13): PTO reads to HR, Admin and the employee alone; the approver reads a request on Time Awaiting Approval.
  timeOff: ['hr', 'self'],
  place: ['hr', 'chain', 'self'],
  access: ['hr', 'self'],
  // 🔒 Decision 3184: the chain reads the Emergency section's rows only through its reason form.
  emergency: ['hr', 'chain', 'self'],
  time: ['hr', 'chain', 'self'],
  // 🔒 Decision 12851: attendance events read to the Discipline section's audience, HR, the chain and the employee.
  attendance: ['hr', 'chain', 'self'],
  trainings: ['hr', 'chain', 'self'],
  // 🔒 Decision 3185: the signed reviews list to all three; the file's bytes keep the HR class's own gate.
  reviews: ['hr', 'chain', 'self'],
  custody: ['hr', 'self'],
};

/** Whether a reader reads a section. */
export function readsSection(reader: EmployeeFileReader, section: EmployeeFileSectionKey): boolean {
  return SECTION_READERS[section].includes(reader);
}

/** The time entries inside Time: the employee and the chain, never HR alone (piece 22). */
export function readsTimeEntries(reader: EmployeeFileReader): boolean {
  return reader === 'self' || reader === 'chain';
}

/** 🔒 The home address inside Place: HR, Admin and the employee, never the chain (decision 3182). */
export function readsHomeAddress(reader: EmployeeFileReader): boolean {
  return reader === 'hr' || reader === 'self';
}

/** The job's private home address, as HR writes it (decision 3182). */
export interface HomeAddressDraft {
  addressLines: string[];
  locality: string;
  administrativeArea: string;
  postalCode: string;
  countryCode: string;
  /** 🔨 The row's version as read (piece 7); an edit naming a version the row no longer holds is refused. Absent before the first write. */
  version?: number;
}

export const EMPTY_HOME_ADDRESS: HomeAddressDraft = { addressLines: [], locality: '', administrativeArea: '', postalCode: '', countryCode: 'US' };

/** HR's write of one job's home address, relative to the API root. */
export function homeAddressPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/home-address`;
}

/** Whether a draft holds an address at all; an empty one writes nothing at hire. */
export function homeAddressGiven(draft: HomeAddressDraft): boolean {
  return draft.addressLines.some((line) => line.trim() !== '') || draft.locality.trim() !== '' || draft.postalCode.trim() !== '';
}

/** The address on one line, the way the Place section draws it. */
export function homeAddressText(draft: Pick<HomeAddressDraft, 'addressLines' | 'locality' | 'administrativeArea' | 'postalCode'>): string {
  const place = [draft.locality, [draft.administrativeArea, draft.postalCode].filter((part) => part && part.trim()).join(' ')].filter((part) => part && part.trim()).join(', ');
  return [...draft.addressLines.filter((line) => line.trim()), place].filter((part) => part !== '').join(', ');
}

/** The Clock Credential History inside Time: HR, Admin and the employee (piece 15). */
export function readsClockCredentials(reader: EmployeeFileReader): boolean {
  return reader === 'hr' || reader === 'self';
}

/**
 * 🔒 The reader, or null for a refusal. The HR pages permission answers
 * first; then the person's own job; then a manager anywhere above it in the
 * chain. Nobody else reads an Employee page.
 */
export function employeeFileReader(input: {
  holdsHrPages: boolean;
  viewerPersonId: string;
  viewerEmploymentId: string;
  subjectPersonId: string;
  /** The employments above this job on `bossEmploymentId` as at today, nearest first. */
  chainAbove: readonly string[];
}): EmployeeFileReader | null {
  if (input.holdsHrPages) return 'hr';
  if (input.viewerPersonId === input.subjectPersonId) return 'self';
  return input.chainAbove.includes(input.viewerEmploymentId) ? 'chain' : null;
}

/** One row of a section, the shape a tile row draws: a label, a value, a caption, the dates and a pill. */
export interface EmployeeFileRow {
  id: string;
  label: string;
  value?: string | null;
  detail?: string | null;
  /** The row's own start, as an instant or a calendar day, drawn in the reader's format. */
  at?: string | null;
  until?: string | null;
  pill?: { text: string; group: 'good' | 'warning' | 'error' | 'inactive' } | null;
  /** The record the row names, where the reader may open it. */
  route?: string | null;
  /** A heading drawn above the row, opening a group inside the section. */
  heading?: string | null;
  /** 🔒 The File the row opens, sent to HR alone, whose class the file proxy gates (decision 3185). */
  fileId?: string | null;
}

export interface EmployeeFileSection {
  key: EmployeeFileSectionKey;
  rows: EmployeeFileRow[];
}

/** One job's file, as the Employee page and My Info draw it. */
export interface EmployeeFile {
  employmentId: string;
  personId: string;
  name: string;
  /** The open position's title, where the job has one. */
  title: string | null;
  startDate: string;
  endDate: string | null;
  /** The job has ended: the page reads read-only, whoever reads it. */
  former: boolean;
  reader: EmployeeFileReader;
  /** Only the sections this reader reads are sent; the rest never leave the server. */
  sections: EmployeeFileSection[];
  /**
   * 🔒 Edit Employee's starting values, the position in force (decision 3181): sent only to a holder of
   * `hr.hire` on a job that has not ended, so the button draws where the write is open.
   */
  edit?: EmployeeEditStart | null;
  /** 🔒 The home address's starting values for HR's edit, sent beside `edit` and to nobody else (decision 3182). */
  home?: HomeAddressDraft | null;
  /** 🔒 The job's active clock credentials for HR's Issue PIN, Register Badge and Revoke, sent beside `edit` and to nobody else (T2069). */
  credentials?: ActiveCredentialRow[] | null;
  /** 🔒 The Emergency section's state (decision 3184): behind a reason for the chain, the contacts to edit for the employee. */
  emergency?: EmergencySection | null;
  /** 🔒 The Reviews section's filing (decision 3185): who may file a signed review on this open job. */
  reviews?: ReviewsSection | null;
  /** 🔒 Change Pay and the waiting change's decision (decision 3781), sent to a holder of `hr.hire` reading as HR and to nobody else. */
  pay?: PaySection | null;
  /** 🔒 Decision 10651: the viewer reads as HR and holds `hr.hire`, which every unemployment-claim route asks. */
  mayRecordClaims?: boolean;
  /** 🔒 Decision 12850: Edit Working Pattern's starting values, sent to HR holding `hr.hire` and to a manager above an open job, and to nobody else. */
  workingPattern?: WorkingPatternSection | null;
  /** 🔒 Decision 13130: Enroll and End under the Job section, sent to HR reading as HR with `hr.hire` and to nobody else. */
  timeOffEnrollment?: TimeOffEnrollmentSection | null;
  /** 🔒 Decision 13251: Cancel Remaining Days under the Time Off section, sent to HR reading as HR with `hr.hire` and to nobody else. */
  timeOffBegun?: TimeOffBegunSection | null;
  /** 🔒 Decision 12930: the approved corrections of the job's time with their answers, sent where the Time section is read (the employee and the chain above the job). */
  timeCorrections?: TimeCorrectionsSection | null;
}

/** My Info's answer: the person's own file, or nothing where they hold no job at our own Company. */
export interface MyInfoAnswer {
  file: EmployeeFile | null;
}
